import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import WellApplicationForm from './WellApplicationForm';
import CompletionReportForm from './CompletionReportForm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Shield, Plus, Search, MoreHorizontal, Pencil, Trash2, FileText, AlertTriangle, BookOpen, Send, CheckCircle, Clock, XCircle, Loader2, ArrowUpCircle, ArrowDownCircle, PauseCircle, Ban, FileDown, ArrowRight, MapPin, FolderOpen, Phone } from 'lucide-react';
import { useCounties } from '@/hooks/useCounties';
import { useNavigate } from 'react-router-dom';
import FormsBattery from '@/components/permits/FormsBattery';
import { normalizeJobCategory } from '@/constants/jobCategories';
import { ActivityLog } from '@/components/ActivityLog';
import DocumentsSection from '@/components/common/DocumentsSection';
import IssuedPermitCard from '@/components/permits/IssuedPermitCard';
import AllFilesTab from '@/components/permits/AllFilesTab';
import FormViewerDialog from '@/components/permits/FormViewerDialog';
import { useJobStatusConfig, jobStatusLabel } from '@/hooks/useJobStatusConfig';
import PhoneLink from '@/components/voice/PhoneLink';
import QuickLinksBar from '@/components/common/QuickLinksBar';
import NotesLogSection from '@/components/common/NotesLogSection';
import ClientNoteCard from '@/components/common/ClientNoteCard';

const STATUS_FLOW = {
  draft: ['submitted_to_county'],
  submitted_to_county: ['approved_by_county', 'needs_more_info'],
  needs_more_info: ['submitted_to_county'],
  approved_by_county: ['active'],
  active: ['pending_customer_info', 'completed'],
  pending_customer_info: ['completed'],
  completed: [],
};

const STATUS_ICONS = {
  submitted_to_county: Send,
  needs_more_info: Clock,
  approved_by_county: CheckCircle,
  active: ArrowUpCircle,
  pending_customer_info: PauseCircle,
  completed: CheckCircle,
  draft: BookOpen,
};

const emptyForm = () => ({
  permit_number: '', county_permit_number: '', receipt_number: '', project_id: '', job_id: '', type: 'construction', type_form_id: '', well_type: '',
  county: '', property_owner: '', property_address: '', township: '', range: '', section: '',
  parcel_id: '', latitude: 0, longitude: 0, notes: '', internal_notes: '', status: 'draft',
});

export default function PermitsPage() {
  const { t } = useTranslation();
  const { hasPermission, user } = useAuth();
  const isAdmin = ['admin', 'superadmin'].includes(user?.role);
  const counties = useCounties();
  const { config: statusConfig } = useJobStatusConfig();
  // TEMA E1 (Alian): the Office Workflow list STATUS column/filters now use
  // the EXACT same status vocabulary as the per-form stepper inside the
  // modal (New/Sent/Signed/Submitted for Approval/Pending/Approved) — no
  // more separate New/In Process/Pending/Complete set that could drift out
  // of sync with what's actually happening inside the permit.
  const permitStatuses = statusConfig?.office_form || [];
  // Forma abierta a pantalla completa (Excel: "I just want to see the PDF
  // file form already filled").
  const [viewerForm, setViewerForm] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const openProjectFromRef = (permit, e) => {
    if (e) e.stopPropagation();
    if (permit.project_id) navigate(`/projects/${permit.project_id}`);
    else openEdit(permit);
  };
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [saving, setSaving] = useState(false);

  // Form tabs & split-screen
  const [activeTab, setActiveTab] = useState('details');
  // Prompt AC CAMBIO 2 (2026-09-21): contador de la pestaña Files, medido
  // aparte de DocumentsSection para que se vea aunque esa pestaña no este activa.
  const [filesCount, setFilesCount] = useState(0);
  const [splitScreen, setSplitScreen] = useState(false);
  const [splitPdfType, setSplitPdfType] = useState('well_app');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (search) params.search = search;
      const [permitsRes, projRes, jobsRes, contactsRes, estRes] = await Promise.all([
        api.get('/permits', { params }),
        api.get('/projects?page_size=200').catch(() => ({ data: { items: [] } })),
        api.get('/jobs?page_size=200').catch(() => ({ data: { items: [] } })),
        api.get('/contacts?page_size=500').catch(() => ({ data: [] })),
        api.get('/estimates').catch(() => ({ data: [] })),
      ]);
      setPermits(Array.isArray(permitsRes.data) ? permitsRes.data : permitsRes.data?.items || []);
      setProjects((projRes.data.items || projRes.data) || []);
      setJobs((jobsRes.data.items || jobsRes.data) || []);
      setContacts(Array.isArray(contactsRes.data) ? contactsRes.data : contactsRes.data.items || []);
      setEstimates(Array.isArray(estRes.data) ? estRes.data : estRes.data.items || []);
    } catch (err) {
      toast.error(t('errors.fetchingError'));
    }
    setLoading(false);
  }, [filter, search, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Handle URL param from Job page: ?project_id=xxx — opens the permit tied
  // to that project. Alian: "al abrir el permiso, no quiero que aparezca un
  // PDF de una vez — quiero ver el numero y los botones primero". Always
  // lands on the 'details' overview; any form (Well App / Completion
  // Report / NOC...) opens ONLY when Alian clicks its colored button.
  useEffect(() => {
    const projectId = searchParams.get('project_id');
    if (projectId && !searchParams.get('action') && permits.length > 0) {
      const permit = permits.find(p => p.project_id === projectId);
      if (permit) {
        setEditing(permit);
        setForm({
          permit_number: permit.permit_number || '',
          county_permit_number: permit.county_permit_number || '',
          receipt_number: permit.receipt_number || '',
          project_id: permit.project_id || '',
          job_id: permit.job_id || '',
          type: permit.type || 'construction',
          type_form_id: permit.type_form_id || '',
          well_type: permit.well_type || '',
          county: permit.county || '',
          property_owner: permit.property_owner || '',
          property_address: permit.property_address || '',
          township: permit.township || '',
          range: permit.range || '',
          section: permit.section || '',
          parcel_id: permit.parcel_id || '',
          latitude: permit.latitude || 0,
          longitude: permit.longitude || 0,
          notes: permit.notes || '',
          internal_notes: permit.internal_notes || '',
          status: permit.status || 'draft',
        });
        setActiveTab('details');
        setSplitScreen(false);
        setDialog(true);
      }
    }
  }, [searchParams, permits]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle URL param: ?action=create&job_id=xxx → open new permit pre-populated from job
  useEffect(() => {
    const action = searchParams.get('action');
    const jobId = searchParams.get('job_id');
    const projectId = searchParams.get('project_id');
    if (action === 'create') {
      setEditing(null);
      const baseForm = emptyForm();
      if (projectId) baseForm.project_id = projectId;
      if (jobId) baseForm.job_id = jobId;
      setForm(baseForm);
      setActiveTab('details');
      setSplitScreen(false);
      setDialog(true);
      // Auto-fill from job
      if (jobId) {
        handleJobChange(jobId);
      } else if (projectId) {
        handleProjectChange(projectId);
      }
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open an existing permit for editing when navigated with ?open=<permitId>
  // (e.g. from the Project Detail "view permit" action). Fetch DIRECTLY by
  // id instead of looking it up in the possibly-filtered `permits` list —
  // a status filter or search term active on this page could hide the
  // target permit, silently leaving the user on the plain list (TEMA B1).
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      setSearchParams(prev => {
        const p = new URLSearchParams(prev);
        p.delete('open');
        return p;
      }, { replace: true });
      api.get(`/permits/${openId}`).then(res => {
        openEdit(res.data);
      }).catch(() => {
        toast.error('No se pudo abrir el permiso');
      });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setActiveTab('details');
    setSplitScreen(false);
    setDialog(true);
  };

  const openEdit = async (permit) => {
    setEditing(permit);
    const base = {
      permit_number: permit.permit_number || '',
      county_permit_number: permit.county_permit_number || '',
      receipt_number: permit.receipt_number || '',
      project_id: permit.project_id || '',
      job_id: permit.job_id || '',
      type: permit.type || 'construction',
      type_form_id: permit.type_form_id || '',
      well_type: permit.well_type || '',
      county: permit.county || '',
      property_owner: permit.property_owner || '',
      property_address: permit.property_address || '',
      township: permit.township || '',
      range: permit.range || '',
      section: permit.section || '',
      parcel_id: permit.parcel_id || '',
      latitude: permit.latitude || 0,
      longitude: permit.longitude || 0,
      notes: permit.notes || '',
      internal_notes: permit.internal_notes || '',
      status: permit.status || 'draft',
    };
    setForm(base);
    setActiveTab('details');
    setSplitScreen(false);
    setDialog(true);
    // Auto-populate missing fields from project/data-flow
    if (permit.project_id && (!base.property_owner || !base.property_address || !base.parcel_id)) {
      try {
        const res = await api.get(`/data-flow/from-project/${permit.project_id}`);
        const d = res.data;
        setForm(prev => ({
          ...prev,
          county: prev.county || d.county || '',
          property_address: prev.property_address || d.well_address || '',
          parcel_id: prev.parcel_id || d.parcel_id || '',
          latitude: (prev.latitude && prev.latitude !== 0) ? prev.latitude : (d.latitude || 0),
          longitude: (prev.longitude && prev.longitude !== 0) ? prev.longitude : (d.longitude || 0),
          township: prev.township || d.township || '',
          range: prev.range || d.range || '',
          section: prev.section || d.section || '',
          property_owner: prev.property_owner || d.property_info?.owner_name || d.customer_name || '',
        }));
      } catch { /* silent - fallback to stored values */ }
    }
  };

  // Auto-fill from project via data flow
  const handleProjectChange = async (projectId) => {
    setForm(prev => ({ ...prev, project_id: projectId }));
    if (!projectId) return;
    try {
      const res = await api.get(`/data-flow/from-project/${projectId}`);
      const d = res.data;
      setForm(prev => ({
        ...prev,
        project_id: projectId,
        county: d.county || prev.county,
        property_address: d.well_address || prev.property_address,
        parcel_id: d.parcel_id || prev.parcel_id,
        latitude: d.latitude || prev.latitude,
        longitude: d.longitude || prev.longitude,
        township: d.township || prev.township,
        range: d.range || prev.range,
        section: d.section || prev.section,
        property_owner: d.property_info?.owner_name || d.customer_name || prev.property_owner,
        well_location: d.location || prev.well_location,
        // Auto-fill Work Type / Well Type from the linked project/estimate —
        // Alian: "no me hagas poner de nuevo el Type of Work ni el Well Type,
        // eso ya lo di al principio". Read-only display below uses these.
        type: d.well_info?.work_type || d.type || prev.type,
        well_type: d.well_info?.well_use || prev.well_type,
      }));
      toast.info(t('permits.autoFilled'));
    } catch {
      // Fallback to local data
      const proj = projects.find(p => p.id === projectId);
      if (proj?.location) {
        const loc = proj.location;
        setForm(prev => ({
          ...prev,
          project_id: projectId,
          county: loc.county || prev.county,
          property_address: loc.address || prev.property_address,
          parcel_id: loc.parcel_id || prev.parcel_id,
          latitude: loc.latitude || prev.latitude,
          longitude: loc.longitude || prev.longitude,
          type: proj.type || prev.type,
        }));
      }
    }
  };

  // Auto-fill from job via data flow
  const handleJobChange = async (jobId) => {
    setForm(prev => ({ ...prev, job_id: jobId }));
    if (!jobId) return;
    try {
      const res = await api.get(`/data-flow/from-job/${jobId}`);
      const d = res.data;
      setForm(prev => ({
        ...prev,
        job_id: jobId,
        project_id: d.project_id || prev.project_id,
        county: d.county || prev.county,
        property_address: d.well_address || prev.property_address,
        parcel_id: d.parcel_id || prev.parcel_id,
        latitude: d.latitude || prev.latitude,
        longitude: d.longitude || prev.longitude,
        township: d.township || prev.township,
        range: d.range || prev.range,
        section: d.section || prev.section,
        property_owner: d.property_info?.owner_name || d.property_owner || d.customer_name || prev.property_owner,
        well_location: d.location || prev.well_location,
        type: d.well_info?.work_type || (d.type === 'new_well' || d.type === 'new_construction' ? 'construction' : d.type) || prev.type,
        well_type: d.well_info?.well_use || prev.well_type,
      }));
      toast.info(t('permits.autoFilled'));
    } catch { /* ignore */ }
  };

  const handleSave = async (closeAfter = false) => {
    setSaving(true);
    try {
      if (editing) {
        const res = await api.put(`/permits/${editing.id}`, form);
        toast.success(t('success.updated'));
        // Alian (video): "Save" keeps the modal open on the same screen;
        // "Save & Close" (E2) closes it. Neither ever force-navigates away
        // from where the user was.
        setEditing(res.data);
        fetchData();
        if (closeAfter) setDialog(false);
      } else {
        const res = await api.post('/permits', form);
        toast.success(t('success.created'));
        fetchData();
        if (closeAfter) {
          setDialog(false);
        } else {
          // Flip straight into the tabbed edit view of the just-created
          // permit instead of closing — same "stay in place" rule as editing.
          await openEdit(res.data);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
    setSaving(false);
  };

  // Generate the Permit Application PDF (page 1 = data, page 2 = well location map)
  // Per Alian's video: "ese mapa que yo le acabé de poner el punto aquí, eso vendría
  // estando entonces en la segunda página".
  const downloadPermitApplicationPdf = async (permit) => {
    const tId = toast.loading('Generando Permit Application PDF…');
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('bw_token');
      const res = await fetch(`${apiUrl}/api/permits/${permit.id}/application-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PermitApplication_${permit.permit_number || permit.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF generado y descargado', { id: tId });
    } catch (err) {
      toast.error('Error generando el PDF', { id: tId });
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.put(`/permits/${id}`, { status: newStatus });
      toast.success(t('success.updated'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  // Office Workflow record-level status (New/Sent/Signed/Submitted for
  // Approval/Pending/Approved) is now 100% derived from the permit's own
  // forms (TEMA E1) — no manual override here anymore, see permits.py
  // compute_office_status/sync_office_status.

  // "Work Site Location" is clickable everywhere (Home row + identity
  // header) → opens the Map centered there (Alian's Excel "Home" sheet).
  const goToMapLocation = (permit) => {
    const lat = permit.latitude;
    const lng = permit.longitude;
    if (!lat || !lng) { toast.info(t('permits.noCoordinates', 'No coordinates saved for this location yet')); return; }
    const params = new URLSearchParams({ lat, lng, address: permit.property_address || '', label: 'Work Site Location' });
    navigate(`/maps?${params.toString()}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    try {
      await api.delete(`/permits/${id}`);
      toast.success(t('success.deleted'));
      fetchData();
    } catch (err) { toast.error(t('errors.deletingError')); }
  };

  const handleSaveWellApp = async (formData) => {
    if (!editing) return;
    try {
      await api.post(`/permits/${editing.id}/well-application`, formData);
      toast.success('Well Application saved');
      const updated = await api.get(`/permits/${editing.id}`);
      setEditing(updated.data);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error saving'); }
  };

  const handleSaveCompReport = async (formData) => {
    if (!editing) return;
    try {
      await api.post(`/permits/${editing.id}/completion-report`, formData);
      toast.success('Completion Report saved');
      const updated = await api.get(`/permits/${editing.id}`);
      setEditing(updated.data);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error saving'); }
  };

  // Refresh the permit being edited (used after Save & Send / Mark Granted
  // on the battery or the structured forms) without closing the dialog.
  const refreshEditingPermit = async () => {
    if (!editing) return;
    try {
      const res = await api.get(`/permits/${editing.id}`);
      setEditing(res.data);
    } catch { /* non-blocking */ }
    fetchData();
  };

  // Prompt AC CAMBIO 2 (2026-09-21): pestaña Notes — lista de entradas, mas
  // reciente arriba, mismo patron que jobs.notes_log.
  // Prompt AD (2026-09-21): sumamos note_type (General/Customer/Internal/
  // Follow Up/System) para la pastilla + filtros del mockup del permiso.
  const handleAddPermitNote = async (text, noteType = 'general') => {
    if (!editing) return;
    const entry = {
      id: (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`),
      text,
      note_type: noteType,
      author_id: user?.id || '',
      author_name: user?.name || '',
      created_at: new Date().toISOString(),
    };
    try {
      const res = await api.put(`/permits/${editing.id}`, { notes_log: [...(editing.notes_log || []), entry] });
      setEditing(res.data);
      fetchData();
      toast.success(t('success.updated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  // Prompt AC CAMBIO 2: "Client Note" guarda al vuelo (onBlur), fuera de las
  // pestañas — independiente del boton Save de la ficha.
  const handleClientNoteBlur = async () => {
    if (!editing) return;
    if (form.notes === (editing.notes || '')) return;
    try {
      const res = await api.put(`/permits/${editing.id}`, { notes: form.notes });
      setEditing(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  // Prompt AC CAMBIO 2: contador de Files (n) — independiente de la pestaña activa.
  useEffect(() => {
    if (!editing?.id) { setFilesCount(0); return; }
    api.get(`/files?entity_type=permit&entity_id=${editing.id}`)
      .then(r => setFilesCount((r.data || []).length))
      .catch(() => setFilesCount(0));
  }, [editing?.id]);

  const getStatusActions = (permit) => {
    const flow = STATUS_FLOW[permit.status] || [];
    return flow.map(s => ({ status: s, label: t(`permits.statuses.${s}`, s), icon: STATUS_ICONS[s] || ArrowUpCircle }));
  };

  const getProjectName = (id) => { const p = projects.find(pr => pr.id === id); return p ? (p.name || p.title || id) : '-'; };
  // Fix Y CAMBIO 2 (2026-09-18): en el Office Workflow, el identificador de la
  // referencia debe ser el numero de Office Work, NUNCA el de proyecto
  // (PRJ-...) — Alian: "aqui tiene que estar el numero de Officework, porque
  // no puedo confundirme".
  // CORRECCION Prompt AA (2026-09-18): CAMBIO 2 apuntaba al campo equivocado
  // (project.office_number, 0/63 proyectos cargados → siempre guion). El
  // campo real es owf_number, del PROPIO permiso (14/14 permisos lo tienen:
  // OWF-2026-001..014). Se lee directo de `permit`, ya no de `projects`.
  // Sin owf_number se muestra un guion — no se genera numeracion nueva, no
  // se escribe nada en la base.
  const getOfficeNumber = (permit) => permit?.owf_number || '-';
  const getContact = (permit) => {
    const proj = projects.find(p => p.id === permit.project_id);
    if (proj) return contacts.find(c => c.id === proj.customer_id);
    return null;
  };
  const getEstimate = (permit) => {
    // First try: from job's source estimate
    const job = jobs.find(j => j.id === permit.job_id);
    if (job?.source_estimate_id) {
      const est = estimates.find(e => e.id === job.source_estimate_id);
      if (est) return est;
    }
    // Second try: from project's estimates (newest first)
    if (permit.project_id) {
      const projectEstimates = estimates.filter(e => e.project_id === permit.project_id);
      if (projectEstimates.length > 0) return projectEstimates[0];
    }
    return null;
  };
  // The Completion Report / Cutting Log live on the WELL CONSTRUCTION job,
  // which is a separate job from the "permits" admin job this Permit may be
  // linked to via job_id (mirrors backend routes/permit_pdf.py::_load_permit_context).
  const getJob = (permit) => {
    const wcJob = jobs.find(j => j.project_id === permit.project_id && normalizeJobCategory(j.type) === 'well_construction');
    return wcJob || jobs.find(j => j.id === permit.job_id);
  };

  const selectedProject = projects.find(p => p.id === form.project_id);
  const selectedContact = editing ? getContact(editing) : (selectedProject ? contacts.find(c => c.id === selectedProject.customer_id) : null);
  // For new permits: find estimate by project_id; for editing: use getEstimate
  const selectedEstimate = editing 
    ? getEstimate(editing) 
    : (form.project_id ? estimates.find(e => e.project_id === form.project_id) : null);
  const selectedJob = editing ? getJob(editing) : null;
  const readOnlyWorkTypeLabel = form.type
    ? t(`projects.types.${form.type}`, t(`permits.types.${form.type}`, form.type))
    : '-';

  const statusCounts = {};
  permitStatuses.forEach(s => statusCounts[s.key] = 0);
  statusCounts.all = permits.length;
  permits.forEach(p => { const k = p.office_status || 'draft'; if (statusCounts[k] !== undefined) statusCounts[k]++; });

  // NOTE: "Well Application" / "Completion Report" are intentionally NOT
  // top-level tabs anymore. Alian: "no quiero ver un PDF que no elegi, yo
  // hago click en el boton de la forma que quiero" — those two forms open
  // ONLY via their colored button in FormsBattery below, never by default.
  const TABS = [
    { key: 'details', label: t('common.details') },
    { key: 'files', label: `Files (${filesCount})` },
    { key: 'notes', label: `${t('permits.tabNotes')} (${(editing?.notes_log || []).length})` },
    { key: 'issued_permit', label: 'Issued Permit' },
  ];

  // Home list (Alian, video "Simplificación de primera página"): solo
  // Reference # · Customer · Teléfono · Work Site Location · Status. Se quitó
  // la matriz de una columna por forma ("en esta primera hoja no quiero ver
  // absolutamente nada más que esto hasta el status").

  return (
    <div className="space-y-4" data-testid="permits-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#003366]" /> {t('permits.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('permits.totalPermits', { count: permits.length })}</p>
        </div>
        {hasPermission('permits', 'create') && <Button onClick={openNew} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="new-permit-btn">
          <Plus className="w-4 h-4 mr-2" /> {t('permits.newPermit')}
        </Button>}
      </div>

      {/* Status Tabs — mirrors the exact per-form status vocabulary
          (New/Sent/Signed/Submitted for Approval/Pending/Approved),
          configurable in Settings > Job Status > Office Forms. */}
      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="permit-status-tabs">
        <button onClick={() => setFilter('all')} data-testid="permit-tab-all"
          className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === 'all' ? 'bg-[#003366] text-white shadow-sm' : 'text-gray-500 hover:bg-gray-100'}`}>
          {t('common.all', 'All')} {statusCounts.all > 0 ? `(${statusCounts.all})` : ''}
        </button>
        {permitStatuses.map(s => (
          <button key={s.key} onClick={() => setFilter(s.key)} data-testid={`permit-tab-${s.key}`}
            className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === s.key ? 'bg-[#003366] text-white shadow-sm' : 'text-gray-500 hover:bg-gray-100'}`}>
            {jobStatusLabel(t, s)} {statusCounts[s.key] > 0 ? `(${statusCounts[s.key]})` : ''}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('permits.searchPlaceholder')} className="pl-9" data-testid="permit-search" />
      </div>

      {/* Table — Ref# + one column per form with its status (Alian: "aqui
          solo voy a ver el status de cada papel de cada proyecto") */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : permits.length === 0 ? (
        <div className="text-center py-12 text-gray-400">{t('common.noResults')}</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-100">
                  <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 sticky left-0 bg-gray-50/80">{t('permits.battery.referenceNumber')}</th>
                  <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">{t('permits.customerName', 'Customer Name')}</th>
                  <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">{t('common.phone', 'Phone')}</th>
                  <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">{t('permits.workSiteLocation', 'Work Site Location')}</th>
                  <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">{t('common.status', 'Status')}</th>
                  <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {permits.map(permit => {
                  const contact = getContact(permit);
                  const customerName = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.company_name : '';
                  const owfStatus = permitStatuses.find(s => s.key === (permit.office_status || 'draft'));
                  return (
                  <tr key={permit.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => openEdit(permit)} data-testid={`permit-row-${permit.id}`}>
                    <td className="px-4 py-3 sticky left-0 bg-white">
                      <button
                        type="button"
                        onClick={(e) => openProjectFromRef(permit, e)}
                        title={permit.project_id ? 'Open project' : ''}
                        className="text-sm font-mono font-bold text-[#003366] hover:underline text-left"
                        data-testid={`permit-ref-${permit.id}`}
                      >
                        {getOfficeNumber(permit)}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 truncate max-w-[160px]" data-testid={`permit-customer-${permit.id}`}>{customerName || '-'}</td>
                    <td className="px-4 py-3 text-sm" onClick={e => e.stopPropagation()}>
                      {contact?.phone ? (
                        <PhoneLink
                          number={contact.phone}
                          name={customerName}
                          testId={`permit-phone-${permit.id}`}
                        />
                      ) : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 truncate max-w-[220px]">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); goToMapLocation(permit); }}
                        disabled={!permit.latitude || !permit.longitude}
                        className="inline-flex items-center gap-1 text-[#003366] hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-default text-left"
                        data-testid={`permit-worksite-${permit.id}`}
                      >
                        <MapPin className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{permit.property_address || '-'}</span>
                      </button>
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${owfStatus?.key === 'granted' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-[#003366] border-blue-200'}`} data-testid={`permit-office-status-${permit.id}`}>
                        {owfStatus ? jobStatusLabel(t, owfStatus) : t('common.status', 'Status')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`permit-actions-${permit.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(permit)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadPermitApplicationPdf(permit)} data-testid={`permit-app-pdf-${permit.id}`}>
                            <FileDown className="w-3.5 h-3.5 mr-2 text-blue-600" />Permit Application PDF
                          </DropdownMenuItem>
                          {getStatusActions(permit).length > 0 && <DropdownMenuSeparator />}
                          {getStatusActions(permit).map(action => (
                            <DropdownMenuItem key={action.status} onClick={() => handleStatusChange(permit.id, action.status)}>
                              <action.icon className="w-3.5 h-3.5 mr-2" />{action.label}
                            </DropdownMenuItem>
                          ))}
                          {hasPermission('permits', 'delete') && <><DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDelete(permit.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem></>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {permits.map(permit => {
              const contact = getContact(permit);
              const customerName = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.company_name : '';
              const owfStatus = permitStatuses.find(s => s.key === (permit.office_status || 'draft'));
              return (
              <div key={permit.id} className="p-4 hover:bg-gray-50/50 transition-colors" onClick={() => openEdit(permit)} data-testid={`permit-card-${permit.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <button
                    type="button"
                    onClick={(e) => openProjectFromRef(permit, e)}
                    className="text-sm font-mono font-bold text-[#003366] hover:underline"
                    data-testid={`permit-card-ref-${permit.id}`}
                  >
                    {getOfficeNumber(permit)}
                  </button>
                  {owfStatus && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border bg-blue-50 text-[#003366] border-blue-200">
                      {jobStatusLabel(t, owfStatus)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  <p>{customerName || '-'}</p>
                  {contact?.phone && (
                    <PhoneLink
                      number={contact.phone}
                      name={customerName}
                      testId={`permit-card-phone-${permit.id}`}
                      className="flex"
                    />
                  )}
                  <p className="flex items-center gap-1"><MapPin className="w-3 h-3 flex-shrink-0" /> {permit.property_address || '-'}</p>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialog} onOpenChange={(open) => { if (!open) setDialog(false); }}>
        <DialogContent className={`${splitScreen ? 'sm:max-w-[95vw] h-[90vh]' : 'sm:max-w-[680px] h-[90vh] max-h-[90vh]'} overflow-hidden flex flex-col`} data-testid="permit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#003366] to-[#005599] flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              {editing ? `${t('common.edit')} ${editing.permit_number || editing.permit_number_auto || ''}` : t('permits.newPermit')}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <QuickLinksBar
              entityType="permit"
              entityId={editing.id}
              customer={selectedContact}
              showCustomerProfile
              className="px-1"
              testId="permit-quick-links"
            />
          )}

          {/* Status Banners */}
          {editing?.status === 'abandoned' && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm flex-shrink-0" data-testid="abandoned-banner">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <div>
                <span className="text-amber-700 font-medium">{t('statusBadges.abandoned')}</span>
                {editing.abandonment_reason && <p className="text-amber-600 text-xs mt-0.5">{editing.abandonment_reason}</p>}
              </div>
            </div>
          )}
          {editing?.status === 'cancelled' && (
            <div className="flex items-center gap-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg text-sm flex-shrink-0" data-testid="cancelled-banner">
              <Ban className="w-4 h-4 text-rose-600 flex-shrink-0" />
              <div>
                <span className="text-rose-700 font-medium">{t('statusBadges.cancelled')}</span>
                {editing.cancellation_reason && <p className="text-rose-600 text-xs mt-0.5">{editing.cancellation_reason}</p>}
              </div>
            </div>
          )}

          {/* Identity header — ALWAYS visible at the top, no matter which
              tab is active. Alian (video "Ajustes de referencia y datos del
              diseño"): quitar el bloque de Reference # (duplica el número
              de proyecto que ya se ve en la lista) y el nombre+dirección
              del cliente (ya arriba en el título/lista) — solo el teléfono
              queda, en la misma línea que OWF/Office #.
              Fix Y CAMBIO 3 (2026-09-18): Office #: X quedó duplicado —
              vuelve a aparecer aquí junto al teléfono, repitiendo lo que ya
              se ve en la columna Reference # de la lista (CAMBIO 2). Alian:
              "quítame este número de aquí, déjame este aquí" — se saca de
              este encabezado, queda solo en la lista. OWF # (identificador
              distinto) se mantiene intacto. */}
          {editing && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3 flex-shrink-0" data-testid="permit-identity-header">
              <div className="flex items-center gap-2 flex-wrap">
                {editing?.owf_number && (
                  <span className="text-xs font-mono font-bold text-white bg-[#003366] rounded px-2 py-1" data-testid="permit-owf-number-badge">
                    {editing.owf_number}
                  </span>
                )}
              </div>
              {selectedContact?.phone && (
                <PhoneLink
                  number={selectedContact.phone}
                  name={[selectedContact.first_name, selectedContact.last_name].filter(Boolean).join(' ') || selectedContact.company_name}
                  testId="permit-customer-phone"
                  className="text-xs"
                />
              )}
            </div>
          )}

          {/* Permit Number — Alian (E4): "lo quiero bien visible, arriba".
              Always visible regardless of tab, right under the identity
              header (Receipt No. removed per his request). */}
          {editing && (
            <div className="flex items-center gap-2 flex-shrink-0" data-testid="permit-number-bar">
              <Label className="text-xs font-semibold text-gray-500 whitespace-nowrap">Permit Number</Label>
              <Input
                value={form.county_permit_number}
                onChange={e => setForm(prev => ({ ...prev, county_permit_number: e.target.value }))}
                placeholder="e.g. SAR-2026-9988"
                className="font-mono h-8 max-w-[220px] bg-white"
                data-testid="permit-office-permit-number-input"
              />
            </div>
          )}

          {/* Tabs */}
          {editing && (
            <div className="flex gap-1 border-b border-gray-100 pb-1 flex-shrink-0" data-testid="permit-tabs">
              {TABS.map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`permit-tab-${tab.key}`}
                  className={`px-3 py-1.5 rounded-t-lg text-xs font-medium transition-all ${activeTab === tab.key ? 'bg-[#003366] text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          <div className={`flex-1 overflow-y-auto ${splitScreen ? 'flex gap-4' : ''}`}>
            {/* Main Content */}
            <div className={`${splitScreen ? 'flex-1 overflow-y-auto pr-2' : ''} space-y-4 py-2`}>
              {/* Forms Battery — Alian: "necesito tener aqui" every form
                  (Permit Application, NOC, NTO, Completion Report, ...) with
                  its status, in the order defined in Jobs>Forms. Prompt AD
                  (2026-09-21): ahora vive DENTRO de Details (antes estaba
                  siempre visible arriba de las pestañas). */}
              {(activeTab === 'details' || !editing) && (
                <>
                  {/* Prompt AD (2026-09-21) — "PERMIT INFORMATION": County +
                      Permit Number (el numero se sigue editando arriba, en
                      la barra siempre visible que pidio Alian — aca solo
                      queda de referencia). */}
                  {editing && (
                    <Card className="bg-white border-gray-100" data-testid="permit-information-card">
                      <CardContent className="p-3 space-y-2">
                        <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold flex items-center gap-1.5">
                          <Shield className="w-3.5 h-3.5 text-amber-500" /> Permit Information
                        </p>
                        <div className="grid grid-cols-2 gap-2.5">
                          <div className="space-y-1">
                            <Label className="text-[10px] text-gray-500 uppercase">County</Label>
                            <Select value={form.county || '_none'} onValueChange={v => setForm(prev => ({ ...prev, county: v === '_none' ? '' : v }))}>
                              <SelectTrigger className="h-9 text-xs" data-testid="permit-info-county-select"><SelectValue placeholder="Select county" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">-- {t('common.none')} --</SelectItem>
                                {counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px] text-gray-500 uppercase">Permit Number</Label>
                            <Input className="h-9 text-xs font-mono bg-gray-50 text-gray-600" value={form.county_permit_number} readOnly data-testid="permit-info-number-display" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {editing && (
                    <FormsBattery
                      permit={editing}
                      onOpenForm={(f) => setViewerForm(f)}
                      onRefreshPermit={refreshEditingPermit}
                    />
                  )}
                </>
              )}
              {(activeTab === 'details' || !editing) && (
                <>
                  {/* Project & Job Linking + Office data — only shown while
                      manually creating a brand-new permit (needs project_id
                      to be usable at all). Removed from the EXISTING-permit
                      Details view per Alian: "menos es mejor" — Details now
                      shows only the compact paperwork list below. */}
                  {!editing && (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">{t('common.project')}</Label>
                        <Select value={form.project_id || '_none'} onValueChange={v => handleProjectChange(v === '_none' ? '' : v)}>
                          <SelectTrigger data-testid="permit-project-select"><SelectValue placeholder={t('permits.selectProject')} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">-- {t('common.none')} --</SelectItem>
                            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name || p.title}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <p className="text-[11px] text-gray-400">
                          El permiso se vincula al proyecto (y a su estimado/invoice); no hace falta elegir un trabajo.
                        </p>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">{t('permits.clientNotes')}</Label>
                        <Textarea value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} rows={2} placeholder={t('common.notesPlaceholder')} />
                      </div>
                    </>
                  )}

                  {/* Compact paperwork list — promoted from the "All Files" tab
                      per Alian: "aqui va a estar TODO el papeleo de la
                      oficina; esta va a ser mi ventana de oficina". All Files
                      tab itself is left untouched below. */}
                  {editing && (
                    <AllFilesTab
                      permit={editing}
                      customerName={selectedContact ? ([selectedContact.first_name, selectedContact.last_name].filter(Boolean).join(' ') || selectedContact.company_name) : ''}
                      onOpenForm={(f) => setViewerForm(f)}
                      onRefresh={refreshEditingPermit}
                    />
                  )}
                </>
              )}

              {activeTab === 'files' && editing && (
                <DocumentsSection entityType="permit" entityId={editing.id} onCountChange={setFilesCount} />
              )}

              {activeTab === 'notes' && editing && (
                <div className="space-y-4 py-2">
                  <NotesLogSection
                    notesLog={editing.notes_log || []}
                    onAddNote={handleAddPermitNote}
                    saving={saving}
                    testIdPrefix="permit-notes"
                    showType
                  />
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                      <Shield className="w-3.5 h-3.5 text-amber-500" /> {t('permits.internalNotes')}
                    </Label>
                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-2 mb-1">
                      <p className="text-[10px] text-amber-600">{t('permits.internalNotesPlaceholder')}</p>
                    </div>
                    <Textarea value={form.internal_notes} onChange={e => setForm(prev => ({ ...prev, internal_notes: e.target.value }))} rows={4} placeholder={t('permits.internalNotesPlaceholder')} className="border-amber-200 focus:border-amber-400" data-testid="permit-internal-notes" />
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={() => handleSave(false)} disabled={saving} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="save-notes-btn">
                      {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save Notes
                    </Button>
                  </div>
                  <ActivityLog entityType="permits" entityId={editing.id} />
                </div>
              )}

              {activeTab === 'issued_permit' && editing && (
                <IssuedPermitCard
                  permit={editing}
                  counties={counties}
                  isAdmin={isAdmin}
                  onUpdated={(updated) => {
                    setEditing(updated);
                    setForm(prev => ({
                      ...prev,
                      county_permit_number: updated.county_permit_number || '',
                      receipt_number: updated.receipt_number || '',
                      county: updated.county || prev.county,
                    }));
                    fetchData();
                  }}
                />
              )}
            </div>

            {/* Split Screen - State Form Reference */}
            {splitScreen && (
              <div className="w-[45%] bg-gray-50 rounded-lg border border-gray-200 overflow-hidden flex-shrink-0 flex flex-col" data-testid="split-screen-panel">
                <div className="flex items-center gap-2 p-3 border-b bg-white">
                  <FileText className="w-4 h-4 text-[#003366]" />
                  <h3 className="text-sm font-bold text-gray-800">{t('permits.splitScreenRef')}</h3>
                  <div className="ml-auto flex gap-1">
                    <Button size="sm" variant={splitPdfType === 'well_app' ? 'default' : 'outline'} 
                      className={splitPdfType === 'well_app' ? 'bg-[#003366] text-white h-6 text-[10px]' : 'h-6 text-[10px]'}
                      onClick={() => setSplitPdfType('well_app')} data-testid="split-pdf-well-app">
                      Well Application
                    </Button>
                    <Button size="sm" variant={splitPdfType === 'completion' ? 'default' : 'outline'} 
                      className={splitPdfType === 'completion' ? 'bg-[#003366] text-white h-6 text-[10px]' : 'h-6 text-[10px]'}
                      onClick={() => setSplitPdfType('completion')} data-testid="split-pdf-completion">
                      Completion Report
                    </Button>
                  </div>
                </div>
                <div className="flex-1 overflow-hidden">
                  <iframe
                    src={splitPdfType === 'well_app'
                      ? 'https://customer-assets.emergentagent.com/job_bd5a9aba-171f-472a-8b26-5fbca00a1a2f/artifacts/7yzz134q_Statewide%20Well%20Application%20revised%208-08-10.pdf'
                      : 'https://customer-assets.emergentagent.com/job_bd5a9aba-171f-472a-8b26-5fbca00a1a2f/artifacts/akdfux0y_Complition%20Report%20.pdf'}
                    title={splitPdfType === 'well_app' ? 'Well Application PDF' : 'Completion Report PDF'}
                    className="w-full h-full border-0"
                    data-testid="split-pdf-viewer"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Prompt AC CAMBIO 2 (2026-09-21): "Client Note" — siempre
              visible, fuera de las 3 pestañas, guarda al vuelo (onBlur). */}
          {editing && (
            <div className="flex-shrink-0 px-1">
              <ClientNoteCard
                value={form.notes}
                onChange={(v) => setForm(prev => ({ ...prev, notes: v }))}
                onBlur={handleClientNoteBlur}
                placeholder="Add a note about this permit..."
                testId="permit-client-note"
              />
            </div>
          )}

          {(activeTab === 'details' || !editing) && (
            <DialogFooter className="flex-shrink-0">
              {editing && hasPermission('permits', 'delete') && (
                <Button
                  variant="outline"
                  onClick={() => { handleDelete(editing.id); setDialog(false); }}
                  className="border-rose-300 text-rose-600 hover:bg-rose-50 mr-auto"
                  data-testid="delete-permit-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete Permit
                </Button>
              )}
              <Button variant="outline" onClick={() => setDialog(false)} data-testid="permit-cancel-btn">{t('common.cancel')}</Button>
              <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="border-[#003366]/30 text-[#003366]" data-testid="save-permit-btn">
                {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                {editing ? t('common.save') : t('common.create')}
              </Button>
              <Button onClick={() => handleSave(true)} disabled={saving} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="save-close-permit-btn">
                {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                {editing ? t('common.saveAndClose', 'Save & Close') : t('common.createAndClose', 'Create & Close')}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Visor de forma a PANTALLA COMPLETA — PDF oficial ya relleno, con
          Edit · Download PDF · Send arriba (Excel "Office Work Flow (2)"). */}
      <FormViewerDialog
        open={!!viewerForm}
        onClose={() => { setViewerForm(null); refreshEditingPermit(); }}
        permit={editing}
        form={viewerForm}
        onChanged={refreshEditingPermit}
        editChildren={viewerForm?.kind === 'structured' && editing ? (
          viewerForm.formKey === 'well_application' ? (
            <WellApplicationForm
              permit={editing}
              project={selectedProject}
              contact={selectedContact}
              estimate={selectedEstimate}
              onSave={handleSaveWellApp}
            />
          ) : (
            <CompletionReportForm
              permit={editing}
              project={selectedProject}
              contact={selectedContact}
              estimate={selectedEstimate}
              job={selectedJob}
              onSave={handleSaveCompReport}
            />
          )
        ) : null}
      />
    </div>
  );
}
